import { StandaloneArticleTeaserProps } from '@/components/ArticleTeaser/Default';
import ErrorBoundary from '@/components/ErrorBoundary';
import { RecommendedArticles, RecommendedArticlesProps } from '@/components/RecommendedArticles/RecommendedArticles';
import { RecommendedArticlesHeadlineProps } from '@/components/RecommendedArticles/RecommendedArticles.Headline';
import { RecommendedArticlesListProps } from '@/components/RecommendedArticles/RecommendedArticles.List';
import { RecommendedArticlesListItemProps } from '@/components/RecommendedArticles/RecommendedArticles.List.Item';
import { RecommendedArticlesListItemTeaserProps } from '@/components/RecommendedArticles/RecommendedArticles.List.Item.Teaser';
import { brandConfig } from '@/components/RecommendedArticles/config';
import { createArticleDataStructure } from '@/components/RecommendedArticles/helpers';
import { getRecommendedArticles, readyToFetch } from '@/components/RecommendedArticles/services';
import { useEdge } from '@/components/layout/Edge';
import { LabradorComponent, StandaloneComponent, StandaloneComponentProps } from '@/types/component';
import { useAppState } from 'lib/hooks/useAppState';
import useLazyCallback from 'lib/hooks/useLazyCallback';
import { isClient } from 'lib/utils/isClient';
import logger from 'lib/utils/logger';
import mergeProps from 'lib/utils/mergeProps';
import getConfig from 'modules/config';
import { standaloneComponent } from 'modules/dynamic-components';
import { ReactNode, useCallback, useState } from 'react';
import { createPortal } from 'react-dom';
import { isNotTrue, isNull } from 'typesafe-utils';

export interface StandaloneRecommendedArticlesProps extends StandaloneComponentProps {
  articles?: StandaloneArticleTeaserProps[];
  articlesCount?: number;
  headline?: ReactNode;
  options?: RecommendedArticlesProps & {
    $headline?: RecommendedArticlesHeadlineProps;
    $list?: RecommendedArticlesListProps & {
      $item?: RecommendedArticlesListItemProps & {
        $teaser?: RecommendedArticlesListItemTeaserProps['$standalone']['options'];
      };
    };
  };
}

export const StandaloneRecommendedArticles: StandaloneComponent<StandaloneRecommendedArticlesProps> = ({
  articles,
  articlesCount,
  headline,
  options,
  ...props
}) => {
  if (!articles || articles.length === 0) {
    return null;
  }

  const { $headline: headlineProps, $list, ...baseProps } = options ?? {};
  const { $item, ...listProps } = $list ?? {};
  const { $teaser: listItemTeaserProps, ...listItemProps } = $item ?? {};

  const MIN_ARTICLES_COUNT = 4;
  const ARTICLES_COUNT_OR_MIN = articlesCount ?? MIN_ARTICLES_COUNT;

  return (
    <ErrorBoundary>
      <RecommendedArticles {...baseProps} {...props}>
        <RecommendedArticles.Headline {...headlineProps}>
          {headline ?? 'Rekommenderad läsning'}
        </RecommendedArticles.Headline>

        <RecommendedArticles.List {...listProps}>
          {articles.slice(0, ARTICLES_COUNT_OR_MIN).map((article, index) => (
            <RecommendedArticles.List.Item key={index} {...listItemProps}>
              <RecommendedArticles.List.Item.Teaser
                $standalone={mergeProps({ options: { ...listItemTeaserProps } }, article)}
              />
            </RecommendedArticles.List.Item>
          ))}
        </RecommendedArticles.List>
      </RecommendedArticles>

      {isClient() &&
        brandConfig.showMoreBelowArticle &&
        articles.length > ARTICLES_COUNT_OR_MIN &&
        createPortal(
          <RecommendedArticles {...baseProps} {...props}>
            <RecommendedArticles.Headline {...headlineProps}>{'Mer för dig'}</RecommendedArticles.Headline>

            <RecommendedArticles.List {...listProps}>
              {articles.slice(ARTICLES_COUNT_OR_MIN).map((article, index) => (
                <RecommendedArticles.List.Item key={index} {...listItemProps}>
                  <RecommendedArticles.List.Item.Teaser
                    $standalone={mergeProps({ options: { ...listItemTeaserProps } }, article)}
                  />
                </RecommendedArticles.List.Item>
              ))}
            </RecommendedArticles.List>
          </RecommendedArticles>,
          document.querySelector('#more-recommended-articles-portal') as Element
        )}
    </ErrorBoundary>
  );
};

export const standalone = StandaloneRecommendedArticles;

const DynamicStandaloneRecommendedArticles = standaloneComponent('ContentRecommendedArticles') as typeof standalone;

export type RecommendedArticlesData = Pick<
  StandaloneArticleTeaserProps,
  'caption' | 'headline' | 'description' | 'image' | 'links'
>[];

const LabradorRecommendedArticles: LabradorComponent = () => {
  const [{ pageId }] = useAppState();
  const edgeRef = useEdge();

  const [articles, setArticles] = useState<RecommendedArticlesData | null>([]);
  const fetchArticles = useCallback(async () => {
    const isReady = await readyToFetch();

    if (!isReady) {
      return setArticles(null);
    }

    const articlesData = await getRecommendedArticles(String(pageId)).catch(logger.catch);

    if (!articlesData) {
      return setArticles(null);
    }

    const transformedData = createArticleDataStructure(articlesData);
    setArticles(transformedData);
  }, [pageId, edgeRef.ref?.current]);

  const ref = useLazyCallback<HTMLDivElement>(fetchArticles, {
    rootMargin: '1000px',
    root: edgeRef.ref?.current,
  });

  if (isNotTrue(getConfig('recommendedArticles.enabled')) || isNull(articles)) return null;

  return articles?.length > 0 ? <DynamicStandaloneRecommendedArticles articles={articles} /> : <div ref={ref} />;
};
export default LabradorRecommendedArticles;
